import {
  DestinationEntityRecordStatus,
  IDestinationEntityJobRunOptions,
  ISourceEntityRecord,
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISource,
  type ITranslationMap,
  type MergeConflictDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Media, Patient } from '@principle-theorem/principle-core';
import {
  type IMedia,
  type IPatient,
  type ITag,
} from '@principle-theorem/principle-core/interfaces';
import {
  FirestoreMigrate,
  ISO_DATE_TIME_FORMAT,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  fromJSON,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toJSON,
  toTimestamp,
  type DocumentReference,
  type INamedDocument,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, uniq } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  CopyFiles,
  StorageBucket,
  StorageFile,
  getBucketStoragePath,
} from '../../../storage';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticePatientAttachment,
  PatientAttachmentSourceEntity,
} from '../../source/entities/patient-attachments';
import {
  CORE_PRACTICE_MEDIA_CATEGORY_TAG_NAME_MAP,
  CorePracticePatientMedia,
  PatientMediaSourceEntity,
} from '../../source/entities/patient-media';
import {
  CorePracticePatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { CorePracticeAttachmentTagMappingHandler } from '../mappings/attachment-to-media-tags';
import { CorePracticeMediaTagMappingHandler } from '../mappings/media-category-to-media-tag';
import { PatientDestinationEntity } from './patients';

export const PATIENT_FILE_RESOURCE_TYPE = 'patientFiles';

export const PATIENT_FILE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_FILE_RESOURCE_TYPE,
    label: 'Patient Files',
    description: '',
  },
});

export interface IPatientFileDestinationRecord {
  fileRefs: DocumentReference<IMedia>[];
}

export interface IPatientFileJobData {
  sourcePatient: IGetRecordResponse<CorePracticePatient>;
  mediaCategories: WithPrimaryKey<ITranslationMap<ITag>>[];
  attachmentCategories: WithPrimaryKey<ITranslationMap<ITag>>[];
  bucketFileSummaryPath: string;
}

interface IFileSummary {
  fileName: string;
  folderPath: string;
}

export interface IPatientFileMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  images: IGetRecordResponse<CorePracticePatientMedia>[];
  attachments: IGetRecordResponse<CorePracticePatientAttachment>[];
  files: IFileSummary[];
  mediaCategories: WithPrimaryKey<ITranslationMap<ITag>>[];
  attachmentCategories: WithPrimaryKey<ITranslationMap<ITag>>[];
}

export class PatientFileDestinationEntity extends BaseDestinationEntity<
  IPatientFileDestinationRecord,
  IPatientFileJobData,
  IPatientFileMigrationData
> {
  destinationEntity = PATIENT_FILE_DESTINATION_ENTITY;

  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientMedia: new PatientMediaSourceEntity(),
    patientAttachments: new PatientAttachmentSourceEntity(),
  };

  customMappings = {
    mediaCategories: new CorePracticeMediaTagMappingHandler(),
    attachmentCategories: new CorePracticeAttachmentTagMappingHandler(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  sourceCountDataAccessor(
    data: IPatientFileJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: IPatientFileJobData): string {
    return data.sourcePatient.record.uid;
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientFileJobData[]> {
    const bucketFileSummaryPath$ = from(
      this._getOrCreateBucketFileSummary(migration)
    );
    return bucketFileSummaryPath$.pipe(
      concatMap((bucketFileSummaryPath) =>
        snapshotCombineLatest([
          this.customMappings.mediaCategories.getRecords(translationMap),
          this.customMappings.attachmentCategories.getRecords(translationMap),
        ]).pipe(
          switchMap((dependencies) =>
            combineLatest([
              this.buildSourceRecordQuery$(
                migration,
                this.sourceEntities.patients,
                runOptions
              ),
              of(dependencies),
            ])
          ),
          map(([sourcePatients, [mediaCategories, attachmentCategories]]) =>
            sourcePatients.map((sourcePatient) => ({
              sourcePatient,
              mediaCategories,
              attachmentCategories,
              bucketFileSummaryPath,
            }))
          )
        )
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientFileJobData
  ): Promise<
    | IPatientFileMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.id;
      const images = await this.sourceEntities.patientMedia.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

      const attachments =
        await this.sourceEntities.patientAttachments.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient`
        );
      }

      const bucketFileSummaryJson = await new StorageBucket(
        migration.configuration.projectId,
        getBucketStoragePath(migration.source)
      ).getFile(data.bucketFileSummaryPath, '');

      const files = getPatientFiles(
        fromJSON<IFileSummary[]>(bucketFileSummaryJson),
        [
          ...attachments.map((source) => ({ id: source.data.id })),
          ...images.map((source) => ({ id: source.data.id })),
        ]
      );

      // eslint-disable-next-line no-console
      console.log(`Found ${files.length} files for patient ${sourcePatientId}`);

      return {
        sourcePatientId: sourcePatientId.toString(),
        patientRef,
        images,
        attachments,
        files,
        mediaCategories: data.mediaCategories,
        attachmentCategories: data.attachmentCategories,
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: IPatientFileMigrationData
  ): IPatientFileMigrationData | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientFileJobData,
    _migrationData: IPatientFileMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    data: IPatientFileJobData,
    migrationData: IPatientFileMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const fileRefs = await this._addMediaToPatient(
        migrationData,
        translationMapHandler,
        migration.source,
        migration.configuration.projectId,
        migration.configuration.destinationBucket,
        migration.configuration.timezone
      );
      return this._buildSuccessResponse(data.sourcePatient, fileRefs);
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  getCopyStorage(
    sourceBucket: string,
    patientRef: DocumentReference<IPatient>,
    projectId: string,
    destinationBucket: string
  ): StorageFile {
    return new StorageFile(
      sourceBucket,
      destinationBucket,
      Patient.storagePath({ ref: patientRef }),
      projectId
    );
  }

  buildSkippedResponse(
    data: IPatientFileJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  private _buildSuccessResponse(
    sourcePatient: IGetRecordResponse<CorePracticePatient>,
    fileRefs: DocumentReference<IMedia>[]
  ): IDestinationEntityRecord<IPatientFileDestinationRecord> {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      data: {
        fileRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.record.id,
      migratedAt: toTimestamp(),
    };
  }

  private _buildErrorResponse(
    sourcePatient: IGetRecordResponse<CorePracticePatient>,
    errorMessage: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sourcePatient.record.id,
      errorMessage,
      failData: {
        patientRef: sourcePatient.record.id,
      },
    };
  }

  private async _getOrCreateBucketFileSummary(
    migration: WithRef<IPracticeMigration>
  ): Promise<string> {
    const fileName = `patient-files-${migration.ref.id}.json`;
    const storageBucket = new StorageBucket(
      migration.configuration.projectId,
      getBucketStoragePath(migration.source)
    );

    try {
      await storageBucket.getFile(fileName, '');
      return fileName;
    } catch {
      const files = await getBucketFiles(
        migration.configuration.projectId,
        getBucketStoragePath(migration.source),
        ''
      );

      await storageBucket.uploadFile(toJSON(files), fileName, '');
      return fileName;
    }
  }

  private async _addMediaToPatient(
    migrationData: IPatientFileMigrationData,
    translationMap: TranslationMapHandler,
    source: ISource,
    projectId: string,
    destinationBucket: string,
    _timezone: Timezone
  ): Promise<DocumentReference<IMedia>[]> {
    const sourcePatientId = migrationData.sourcePatientId;
    const patientRef = migrationData.patientRef;
    const images = migrationData.images;
    const attachments = migrationData.attachments;
    const files = migrationData.files;

    // eslint-disable-next-line no-console
    console.log(
      `Found ${files.length} files for patient ${sourcePatientId} in the bucket`
    );
    if (!files.length) {
      return [];
    }

    const copyStorage = this.getCopyStorage(
      getBucketStoragePath(source),
      patientRef,
      projectId,
      destinationBucket
    );

    // Process multiple batches in parallel, each batch processes sequentially
    const BATCH_SIZE = 10;
    const PARALLEL_BATCHES = 3; // Process 3 batches at once = 30 files in parallel
    const results: DocumentReference<IMedia>[] = [];

    // Create batch groups for parallel processing
    const batches: IFileSummary[][] = [];
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      batches.push(files.slice(i, i + BATCH_SIZE));
    }

    // Process batches in parallel groups
    for (let i = 0; i < batches.length; i += PARALLEL_BATCHES) {
      const parallelBatches = batches.slice(i, i + PARALLEL_BATCHES);

      const parallelResults = await Promise.all(
        parallelBatches.map(async (batch, batchIndex) => {
          const actualBatchNumber = i + batchIndex;

          const batchResults = await resolveSequentially(
            batch,
            async (file) => {
              try {
                const destinationPath = await copyStorage.copy(file.folderPath);
                if (!destinationPath) {
                  throw new Error(`Failed to copy ${file.folderPath}`);
                }

                const matchingImage = images.find(
                  (image) => image.data.id === file.fileName
                );
                const matchingAttachment = attachments.find(
                  (attachment) => attachment.data.id === file.fileName
                );

                if (!matchingImage && !matchingAttachment) {
                  throw new Error(
                    `No matching image or attachment found for ${file.folderPath}`
                  );
                }

                const fileDestinationRef =
                  await translationMap.getDestination<IMedia>(
                    file.folderPath,
                    PATIENT_FILE_RESOURCE_TYPE
                  );

                const createdAt = this._determineCreatedAt(
                  matchingImage,
                  matchingAttachment
                );
                const fileName = this._determineFileName(
                  matchingImage,
                  matchingAttachment
                );
                const tags = compact([
                  this._getTags(
                    migrationData.mediaCategories,
                    migrationData.attachmentCategories,
                    matchingImage,
                    matchingAttachment
                  ),
                ]);

                const mediaRef = await FirestoreMigrate.upsertDoc(
                  Patient.mediaCol({ ref: patientRef }),
                  {
                    ...Media.init({
                      name: fileName,
                      path: destinationPath,
                      tags,
                    }),
                    createdAt,
                  },
                  fileDestinationRef?.id
                );

                if (!fileDestinationRef) {
                  await translationMap.upsert({
                    sourceIdentifier: file.folderPath,
                    destinationIdentifier: mediaRef,
                    resourceType: PATIENT_FILE_RESOURCE_TYPE,
                  });
                }

                return mediaRef;
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error(
                  `Error processing file ${file.folderPath}:`,
                  error
                );
                throw error;
              }
            }
          );

          // eslint-disable-next-line no-console
          console.log(
            `Completed batch ${actualBatchNumber + 1} of ${
              batches.length
            } for patient ${sourcePatientId}`
          );

          return batchResults;
        })
      );

      // Flatten parallel results and add to main results array
      results.push(...parallelResults.flat());
    }

    return results;
  }

  private _determineCreatedAt(
    matchingImage?: IGetRecordResponse<CorePracticePatientMedia>,
    matchingAttachment?: IGetRecordResponse<CorePracticePatientAttachment>
  ): Timestamp {
    if (matchingImage) {
      return toTimestamp(
        moment.utc(matchingImage.data.dateTaken, ISO_DATE_TIME_FORMAT)
      );
    }

    if (matchingAttachment) {
      return toTimestamp(
        moment.utc(matchingAttachment.data.attachmentDate, ISO_DATE_TIME_FORMAT)
      );
    }

    return toTimestamp();
  }

  private _determineFileName(
    matchingImage?: IGetRecordResponse<CorePracticePatientMedia>,
    matchingAttachment?: IGetRecordResponse<CorePracticePatientAttachment>
  ): string {
    if (matchingImage) {
      return matchingImage.data.fileName;
    }

    if (matchingAttachment) {
      return matchingAttachment.data.fileName;
    }

    return '';
  }

  private _getTags(
    mediaCategories: WithPrimaryKey<ITranslationMap<ITag>>[],
    attachmentCategories: WithPrimaryKey<ITranslationMap<ITag>>[],
    matchingImage?: IGetRecordResponse<CorePracticePatientMedia>,
    matchingAttachment?: IGetRecordResponse<CorePracticePatientAttachment>
  ): INamedDocument<ITag> | undefined {
    if (matchingImage && matchingImage.data.categoryId) {
      const category =
        CORE_PRACTICE_MEDIA_CATEGORY_TAG_NAME_MAP[
          matchingImage.data.categoryId
        ];
      const found = mediaCategories.find(
        (mediaCategory) => category === mediaCategory.sourceIdentifier
      );

      if (!found || !found.destinationIdentifier) {
        return;
      }
      return {
        name: found.sourceIdentifier,
        ref: found.destinationIdentifier,
      };
    }

    if (matchingAttachment && matchingAttachment.data.tag) {
      const found = attachmentCategories.find(
        (attachmentCategory) =>
          matchingAttachment.data.tag === attachmentCategory.sourceIdentifier
      );

      if (!found || !found.destinationIdentifier) {
        return;
      }

      return {
        name: found.sourceIdentifier,
        ref: found.destinationIdentifier,
      };
    }
  }
}

export async function getBucketFiles(
  projectId: string,
  bucketName: string,
  bucketPath: string
): Promise<IFileSummary[]> {
  const storage = new CopyFiles(bucketName, bucketPath, '', '', projectId)
    .storage;

  const [files] = await storage.bucket(bucketName).getFiles({
    prefix: bucketPath,
  });

  return uniq(
    compact(
      files.map((file) => {
        const folderParts = file.name.split('/');
        const fileName = folderParts[folderParts.length - 1].split('.')[0];

        return {
          fileName,
          folderPath: file.name,
        };
      })
    )
  );
}

export function getPatientFiles(
  bucketFiles: IFileSummary[],
  sources: { id: string }[]
): IFileSummary[] {
  return uniq(
    compact(
      bucketFiles.filter((file) =>
        sources.some((source) => source.id === file.fileName)
      )
    )
  );
}
